import { match } from 'ts-pattern'
import { Trans, useLingui } from '@lingui/react/macro'

import type { RouterOutput } from '@loc/server'
import type { $Enums } from '@prisma/client'
import type { FileFormValue } from '../../UploadAudio'

import { usePlayAudio } from '@/pages/tad.ai/_hooks/usePlayAudio'
import { useAntdContext } from '@/contexts/AntdContext/hooks'
import { formatAudioTime } from '@/helpers/music'
import PlayIcon from '@/pages/tad.ai/_components/PlayIcon'


import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
import { CssIcon } from '@/components/CssIcon';
type ListItemData = RouterOutput['reference']['history']['data'][number]

export interface EditAudioShowData {
  token: string
  filename: string
  file: string
  originFile?: string
  musicModel: 'V2' | 'V3'
  musicType: $Enums.MusicType
  beginTime: number
  endTime: number
  onSuccess?: () => void
}

interface ListItemProps {
  data: ListItemData
  musicModel: 'V2' | 'V3'
  musicType: $Enums.MusicType
  onDeleteSuccess: () => void
  onUse: (data: FileFormValue) => void
  onEditAudio?: (data: EditAudioShowData) => void
}
function ListItem(props: ListItemProps) {
  const { data, musicModel, musicType, onDeleteSuccess, onUse, onEditAudio } =
    props
  const { t } = useLingui()

  const { modalInstance } = useAntdContext()
  const { mutateAsync: deleteAsync } = api.reference.delete.useMutation({
    onSuccess: () => {
      onDeleteSuccess()
    },
  })
  const apiUtils = api.useUtils()

  const handleUse = (data: ListItemData) => {
    onUse({
      token: data.token,
      filename: data.filename || '',
    })
  }

  const { playing, sound, handlePlay, duration } = usePlayAudio({
    file: data.file || '',
  })

  return (
    <li
      className={cls`bg-f-bg-layout flex items-center justify-between gap-3 rounded-[6px] p-3`}
    >
      <div className={cls`flex flex-1 items-center gap-x-3`}>
        <div
          className={cls`bg-f-bg-container text-f-text-light hover:text-f-primary flex size-10 shrink-0 cursor-pointer items-center justify-center md:size-12`}
          onClick={handlePlay}
        >
          <PlayIcon className={cls`size-5`} playing={playing} />
        </div>
        <div>
          <div
            className={cls`text-f-text-secondary text-xs font-semibold md:text-sm`}
          >
            {data.filename}
          </div>
          <div className={cls`text-f-text-quaternary text-sm`}>
            {formatAudioTime(duration)}
          </div>
        </div>
      </div>
      <div className={cls`flex items-center gap-x-3`}>
        {match(data.status)
          .with('Success', () => (
            <>
              <div
                className={cls`
                  bg-f-bg-active border-f-border-secondary text-f-text-tertiary hover:text-f-primary cursor-pointer rounded border px-3 py-1
                  text-sm font-semibold
                `}
                onClick={() => handleUse(data)}
              >
                <Trans>Use</Trans>
              </div>
              {onEditAudio && (
                <CssIcon
                  className={cls`i-com--edit-alt text-f-text-tertiary hover:text-f-primary size-5 cursor-pointer`}
                  onClick={() => {
                    // 使用当前裁剪后的文件作为编辑源，而非原始文件
                    const currentFile = data.file || ''
                    onEditAudio({
                      token: data.token,
                      filename: data.filename || '',
                      file: currentFile,
                      originFile: currentFile || undefined,
                      musicModel,
                      musicType,
                      beginTime: 0,
                      endTime: 0,
                      onSuccess: () => {
                        apiUtils.reference.history.invalidate()
                      },
                    })
                  }}
                />
              )}
            </>
          ))
          .otherwise(() => (
            <div
              className={cls`text-f-error bg-f-error-bg rounded px-[6px] py-[2px] align-top text-xs`}
            >
              {data.status}
            </div>
          ))}

        <CssIcon
          className={cls`i-com--trash text-f-text-tertiary hover:text-f-primary size-5 cursor-pointer`}
          onClick={() => {
            modalInstance.confirm({
              title: t`Delete Audio`,
              content: t`Are you sure you want to delete this audio?`,
              cancelText: t`Cancel`,
              okText: t`Delete`,
              onOk: async () => {
                await deleteAsync({ token: data.token })
              },
            })
          }}
        />
      </div>
    </li>
  )
}

export default ListItem
